import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { NavigationMenuSelection } from "../interfaces/navigation_menu.interface";

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

export interface QuestionResponseActivityDto {
    id: number;
    description: string;
    userRole?: string | null;
    user_role?: string | null;
    createdAt?: string | null;
    created_at?: string | null;
    userDisplayName?: string | null;
    user_display_name?: string | null;
    questionReference?: string | null;
    question_reference?: string | null;
    section?: string | null;
    target?: string | null;
}

export interface QuestionResponseActivityResponse {
    items: QuestionResponseActivityDto[];
    total: number;
}

@Injectable({ providedIn: "root" })
export class QuestionResponseActivityService {
    private static readonly SECTION_PATTERN = /^[A-Za-z0-9_]+$/;
    private static readonly TARGET_PATTERN = /^[A-Za-z0-9 _.-]+$/;
    private static readonly DEFAULT_PAGE_SIZE = 5;

    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");
    private activeProductLicenseId: number | null = null;

    constructor(private readonly http: HttpClient) {}

    setActiveProductLicenseId(productLicenseId: number | null): void {
        if (productLicenseId === null || productLicenseId === undefined) {
            this.activeProductLicenseId = null;
            return;
        }

        const normalized = Number(productLicenseId);
        this.activeProductLicenseId = Number.isInteger(normalized) && normalized > 0 ? normalized : null;
    }

    listRecentActivity(
        selection: NavigationMenuSelection | null | undefined,
        pageIndex: number,
        productLicenseId?: number
    ): Observable<QuestionResponseActivityResponse> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before loading recent activity."));
        }

        const normalized = this.normalizeSelection(selection ?? null);
        const query: Record<string, string> = {
            productLicenseId: String(licenseId),
            page: String(pageIndex > 0 ? pageIndex : 1),
            pageSize: String(QuestionResponseActivityService.DEFAULT_PAGE_SIZE)
        };

        if (normalized) {
            query["section"] = normalized.section;
            query["target"] = normalized.target;
        }

        const params = new HttpParams({ fromObject: query });

        return this.http
            .get<ApiResponse<QuestionResponseActivityResponse>>(`${this.apiBaseUrl}/activity-tracking/recent`, { params })
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Failed to load recent activity.");
                    }

                    const items = Array.isArray(response.data.items) ? response.data.items : [];
                    const total = Number(response.data.total) || 0;

                    return { items, total };
                })
            );
    }

    private resolveProductLicenseId(productLicenseId?: number): number | null {
        const candidate = productLicenseId ?? this.activeProductLicenseId;
        const normalized = Number(candidate);

        if (!Number.isInteger(normalized) || normalized <= 0) {
            return null;
        }

        return normalized;
    }

    private normalizeSelection(selection: NavigationMenuSelection | null | undefined): NavigationMenuSelection | null {
        if (!selection) {
            return null;
        }

        const section = typeof selection.section === "string" ? selection.section.trim() : "";
        const target = typeof selection.target === "string" ? selection.target.trim() : "";

        if (!section || !target) {
            return null;
        }

        if (!QuestionResponseActivityService.SECTION_PATTERN.test(section)) {
            return null;
        }

        if (!QuestionResponseActivityService.TARGET_PATTERN.test(target)) {
            return null;
        }

        return { section, target };
    }
}
